import { ColorEnum } from '@/constants/color'
import Cutout from '@/features/common/components/Cutout'
import Cart from '@/features/pos/components/Cart'
import { useIsCartInFlightRequests } from '@/features/pos/components/Cart/hooks/cart-optimistic-update-counter'
import { useCartItems } from '@/features/pos/components/Cart/hooks/useCartItems'
import { AutomaticContainerRemovals } from '@/features/pos/components/Cart/utils/automatic-containers'
import { useFormAutomaticContainerRemovalsValue } from '@/features/pos/components/OrderFinalization/form'
import { usePersistAutomaticContainerRemovals } from '@/features/pos/components/OrderFinalization/form/hooks/usePersistAutomaticContainerRemovals'
import { useDeliveryTypeCategories } from '@/features/pos/components/OrderFinalization/hooks/useDeliveryTypeCategories'
import { useIsPricesLoading } from '@/features/pos/components/OrderFinalization/hooks/useIsPricesLoading'
import { usePaymentTypes } from '@/features/pos/components/OrderFinalization/hooks/usePaymentTypes'
import { usePrices } from '@/features/pos/components/OrderFinalization/hooks/usePrices'
import { useReactiveOrderDraft } from '@/features/pos/components/OrderFinalization/hooks/useReactiveOrderDraft'
import { useShouldAttachAutomaticContainers } from '@/features/pos/components/OrderFinalization/hooks/useShouldAttachAutomaticContainers'
import BottomSheet from '@/features/pos/components/OrderFinalization/parts/BottomSheet'
import { PaymentType } from '@/features/pos/components/OrderFinalization/parts/BottomSheet/types'
import FormFields from '@/features/pos/components/OrderFinalization/parts/FormFields'
import { DeliveryPaymentMapping } from '@/features/pos/components/OrderFinalization/types'
import { usePosMode } from '@/features/pos/contexts/PosMode'
import { hexToRgba } from '@/utils/hexToRgba'
import isEqual from 'lodash.isequal'
import { FC, useMemo } from 'react'
import { StyleSheet, View } from 'react-native'

interface ContentProps {
    deliveryPaymentMappings: DeliveryPaymentMapping[]
}

const Content: FC<ContentProps> = ({ deliveryPaymentMappings }) => {
    const posMode = usePosMode()
    const automaticContainerRemovalsHydrated = usePersistAutomaticContainerRemovals({
        defaultDeliveryTypeCategory: deliveryPaymentMappings[0]?.deliveryTypeCategory,
        posMode,
    })
    const deliveryTypeCategories = useDeliveryTypeCategories(deliveryPaymentMappings)

    const { items: cartItems, containerItems, loaded: cartLoaded } = useCartItems()

    const allCartItems = useMemo(
        () => [...cartItems, ...containerItems],
        [cartItems, containerItems]
    )

    const cartEmpty = !allCartItems.length

    const paymentTypes = usePaymentTypes(deliveryPaymentMappings)

    const automaticContainerSetting = useShouldAttachAutomaticContainers()
    const automaticContainerRemovals = useFormAutomaticContainerRemovalsValue()
    const automaticContainerRemovalsReconciled = useMemo(
        () =>
            isEqual(
                AutomaticContainerRemovals.from(automaticContainerRemovals)
                    .reconcileWith(cartItems)
                    .toArray(),
                automaticContainerRemovals
            ),
        [automaticContainerRemovals, cartItems]
    )
    const automaticContainersReady =
        automaticContainerSetting.ready &&
        automaticContainerRemovalsHydrated &&
        automaticContainerRemovalsReconciled
    const readyAutomaticContainerSetting = useMemo(
        () => ({ ...automaticContainerSetting, ready: automaticContainersReady }),
        [automaticContainerSetting, automaticContainersReady]
    )
    const orderDraftLoading = useReactiveOrderDraft(paymentTypes[0], readyAutomaticContainerSetting)
    const isCartInFlightRequests = useIsCartInFlightRequests()

    const isPricesLoading = useIsPricesLoading(orderDraftLoading || !automaticContainersReady)

    const prices = usePrices(isPricesLoading)

    return (
        <>
            <View style={styles.container}>
                <View style={styles.inputsContainer}>
                    <FormFields deliveryTypeCategories={deliveryTypeCategories} />
                </View>
                <Cutout />
                <Cart
                    items={cartItems}
                    containerItems={containerItems}
                    automaticContainersEnabled={
                        automaticContainersReady && automaticContainerSetting.shouldAttach
                    }
                    loaded={cartLoaded}
                    priceItems={prices?.items}
                    totalPrice={prices?.total}
                />
                <BottomSheet
                    cartEmpty={cartEmpty}
                    cartItems={allCartItems}
                    price={prices?.total}
                    priceItems={prices?.items}
                    paymentTypes={paymentTypes as PaymentType[]}
                    shouldAttachAutomaticContainers={automaticContainerSetting.shouldAttach}
                    actionDisabled={
                        isPricesLoading || isCartInFlightRequests || !automaticContainersReady
                    }
                />
            </View>
        </>
    )
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: ColorEnum.WHITE,
        elevation: 4,
        shadowColor: hexToRgba(ColorEnum.Black, 0.25),
        shadowRadius: 8,
        shadowOffset: {
            width: -4,
            height: 0,
        },
        shadowOpacity: 1,
    },
    inputsContainer: {
        alignItems: 'center',
        backgroundColor: ColorEnum.GRAY_100,
        padding: 16,
        gap: 16,
        zIndex: 1,
    },
})

export default Content
